/**
 * Adaptive Tracking Filter (zero-restart)
 *
 * The community background-geolocation plugin does NOT support live option
 * updates — changing distanceFilter at the native layer requires a full
 * watcher restart, which is exactly what we are trying to avoid (every
 * restart is a battery spike on iOS/Android).
 *
 * Strategy: keep ONE native watcher running at the tightest sensible filter
 * (the EN_ROUTE baseline) and apply a software-side "virtual" distance
 * filter on top of every incoming GPS callback. The watcher never changes,
 * but we decide in JS whether each fix is worth processing/saving based on
 * the driver's current movement profile.
 *
 * Adaptive rules (re-evaluated every 60s during EN_ROUTE only):
 *   - speed < 2 m/s and stationary > 2 min → loosen filter to 150–200m
 *   - speed > 8 m/s sustained               → tighten filter to 75m
 *   - moderate movement                     → keep current
 *
 * Safety:
 *   - Config changes are throttled to once per 60s
 *   - No changes happen in IDLE (the watcher is stopped anyway)
 *   - No watcher restart is ever triggered by this module
 */

export type AdaptiveTrackingState = "IDLE" | "ASSIGNED" | "EN_ROUTE";

export interface AdaptiveConfig {
  /** Software-side distance filter in metres */
  distanceFilter: number;
  /** Hint only — high accuracy at the watcher level cannot be retuned live */
  enableHighAccuracy: boolean;
}

const ASSIGNED_FILTER_M = 250;
const EN_ROUTE_BASELINE_M = 75;
const EN_ROUTE_LOOSE_M = 175;

const STATIONARY_SPEED_MPS = 2;
const FAST_SPEED_MPS = 8;
const STATIONARY_DWELL_MS = 2 * 60 * 1000; // 2 min
const SUSTAINED_FAST_MS = 60 * 1000; // 1 min of sustained fast movement
const MIN_CONFIG_CHANGE_MS = 60_000;
const EVAL_INTERVAL_MS = 60_000;

interface SpeedSample {
  speed: number;
  ts: number;
}

export class AdaptiveTrackingFilter {
  private state: AdaptiveTrackingState = "IDLE";
  private currentConfig: AdaptiveConfig = {
    distanceFilter: EN_ROUTE_BASELINE_M,
    enableHighAccuracy: true,
  };

  private lastConfigChangeAt = 0;
  private lastEmittedPos: { lat: number; lng: number; ts: number } | null = null;
  private speedSamples: SpeedSample[] = [];
  private stationarySince: number | null = null;
  private fastSince: number | null = null;
  private evalTimer: ReturnType<typeof setInterval> | null = null;

  /** Called whenever the job-state machine flips. Cheap, no side-effects. */
  setState(next: AdaptiveTrackingState) {
    if (next === this.state) return;
    const prev = this.state;
    this.state = next;

    if (next === "EN_ROUTE") {
      this.currentConfig = { distanceFilter: EN_ROUTE_BASELINE_M, enableHighAccuracy: true };
      this.lastConfigChangeAt = Date.now();
      this.startEvalLoop();
    } else if (next === "ASSIGNED") {
      this.currentConfig = { distanceFilter: ASSIGNED_FILTER_M, enableHighAccuracy: false };
      this.lastConfigChangeAt = Date.now();
      this.stopEvalLoop();
    } else {
      // IDLE — no changes, no loop. The watcher is being torn down by the host.
      this.stopEvalLoop();
      this.reset();
    }

    console.log(
      `[AdaptiveFilter] state ${prev} → ${next}, filter=${this.currentConfig.distanceFilter}m`,
    );
  }

  getConfig(): AdaptiveConfig {
    return { ...this.currentConfig };
  }

  /**
   * Decide whether an incoming GPS fix should be processed (saved to DB,
   * forwarded to listeners). Updates internal speed/movement stats either way.
   */
  shouldEmit(lat: number, lng: number, speedMps: number | null | undefined): boolean {
    const now = Date.now();
    const speed = typeof speedMps === "number" && speedMps >= 0 ? speedMps : null;

    // Track speed history for the eval loop
    if (speed !== null) {
      this.speedSamples.push({ speed, ts: now });
      // Keep only last 5 minutes
      const cutoff = now - 5 * 60 * 1000;
      this.speedSamples = this.speedSamples.filter((s) => s.ts >= cutoff);
    }

    // First fix always emits
    if (!this.lastEmittedPos) {
      this.lastEmittedPos = { lat, lng, ts: now };
      return true;
    }

    // Software distance gate
    const distance = haversineMeters(
      this.lastEmittedPos.lat,
      this.lastEmittedPos.lng,
      lat,
      lng,
    );

    if (distance < this.currentConfig.distanceFilter) {
      return false;
    }

    this.lastEmittedPos = { lat, lng, ts: now };
    return true;
  }

  /** Stop timers; safe to call multiple times. */
  dispose() {
    this.stopEvalLoop();
    this.reset();
  }

  // ─── internal ──────────────────────────────────────────────────────────────

  private reset() {
    this.lastEmittedPos = null;
    this.speedSamples = [];
    this.stationarySince = null;
    this.fastSince = null;
  }

  private startEvalLoop() {
    if (this.evalTimer) return;
    this.evalTimer = setInterval(() => this.evaluate(), EVAL_INTERVAL_MS);
  }

  private stopEvalLoop() {
    if (this.evalTimer) {
      clearInterval(this.evalTimer);
      this.evalTimer = null;
    }
  }

  private evaluate() {
    if (this.state !== "EN_ROUTE") return;

    const now = Date.now();
    if (now - this.lastConfigChangeAt < MIN_CONFIG_CHANGE_MS) return;

    // Need at least one sample in the last 90s to make a decision
    const recent = this.speedSamples.filter((s) => now - s.ts <= 90_000);
    if (recent.length === 0) return;

    const avgSpeed = recent.reduce((acc, s) => acc + s.speed, 0) / recent.length;

    // Update stationary/fast streak markers
    if (avgSpeed < STATIONARY_SPEED_MPS) {
      this.fastSince = null;
      if (this.stationarySince === null) this.stationarySince = now;
    } else if (avgSpeed > FAST_SPEED_MPS) {
      this.stationarySince = null;
      if (this.fastSince === null) this.fastSince = now;
    } else {
      this.stationarySince = null;
      this.fastSince = null;
    }

    let next: AdaptiveConfig | null = null;

    if (this.stationarySince !== null && now - this.stationarySince >= STATIONARY_DWELL_MS) {
      // Loosen — driver is parked / in heavy traffic
      if (this.currentConfig.distanceFilter !== EN_ROUTE_LOOSE_M) {
        next = { distanceFilter: EN_ROUTE_LOOSE_M, enableHighAccuracy: false };
      }
    } else if (this.fastSince !== null && now - this.fastSince >= SUSTAINED_FAST_MS) {
      // Tighten — driver is on the road, sustained pace
      if (this.currentConfig.distanceFilter !== EN_ROUTE_BASELINE_M) {
        next = { distanceFilter: EN_ROUTE_BASELINE_M, enableHighAccuracy: true };
      }
    }

    if (next) {
      const prev = this.currentConfig;
      this.currentConfig = next;
      this.lastConfigChangeAt = now;
      console.log(
        `[AdaptiveFilter] tuned ${prev.distanceFilter}m → ${next.distanceFilter}m ` +
          `(avgSpeed=${avgSpeed.toFixed(1)} m/s, samples=${recent.length})`,
      );
    }
  }
}

function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}
